import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { PORTFOLIO_PAGE, SELL_PAGE } from '../../../constants/pages';
import { ButtonElement } from '../../Button/Button';
import Paragraph from '../../Paragraph/Paragraph';
import themeColors from '../../../assets/styles/themeColors';
import { pxToRem } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import BasicAccountRestristionsModal from '../../Modals/BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';

const SellFromHomeCtaContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: ${({ stacked }) => (stacked ? 'column' : 'row')};
  align-items: ${({ stacked }) => (stacked ? 'stretch' : 'center')};
  justify-content: space-between;
  gap: ${pxToRem(16)};
  padding: ${pxToRem(16)};
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: ${pxToRem(6)};
  margin-bottom: ${({ stacked }) => (stacked ? `${pxToRem(32)}` : '0px')};
`;

const SellFromHomeTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(4)};
`;

const SellFromHomeHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${pxToRem(8)};
`;

const TitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
`;

const SubtitleText = styled(Paragraph)`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
  color: ${themeColors.colorLabel};
`;

const SellFromHomeButton = styled(ButtonElement)`
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(24)};
`;

const NewBadge = styled.span`
  background-color: ${themeColors.colorBrandLight};
  color: ${themeColors.colorBlack};
  font-size: ${pxToRem(14)};
  font-weight: 600;
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(4)} ${pxToRem(8)};
  white-space: nowrap;
`;

const SellFromHomeCta = ({ stacked }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (![PORTFOLIO_PAGE, SELL_PAGE].includes(pathname)) {
    return null;
  }

  return (
    <>
      <SellFromHomeCtaContainer stacked={stacked}>
        <SellFromHomeTextContent>
          <SellFromHomeHeader>
            <TitleText>{t('portfolio.sellFromHome.title')}</TitleText>
            <NewBadge>{t('portfolio.sellFromHome.badge')}</NewBadge>
          </SellFromHomeHeader>
          <SubtitleText color={themeColors.colorTextSecondary}>
            {t('portfolio.sellFromHome.subtitle')}
          </SubtitleText>
        </SellFromHomeTextContent>
        <SellFromHomeButton
          variant="primary"
          textTransform="none"
          isWidthS={stacked}
          onClick={() => setIsModalOpen(true)}
        >
          {t('portfolio.sellFromHome.cta')}
        </SellFromHomeButton>
      </SellFromHomeCtaContainer>

      <BasicAccountRestristionsModal
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        title={t('basicAccountRestrictions.verifyAccountSellFromHome.title')}
        description={t(
          'basicAccountRestrictions.verifyAccountSellFromHome.description',
        )}
        icon={<InfoIcon width={48} height={48} />}
        showDoItLater
      />
    </>
  );
};

SellFromHomeCta.propTypes = {
  stacked: PropTypes.bool,
};

export default SellFromHomeCta;
